<script lang="ts">
  import { readRefusedRows, refusedRowsDetail, refusedRowsFault } from "./refusedRows";
  /**
   * §GEX-2 — LIVE 0DTE DEALER EXPOSURE. R424(2), owner order. Data half: ar's `@c54af27`.
   *
   * Renders ABOVE §STRUCTURE: delivery on top, map below (R387/R408's two-sections law).
   *
   * ⛔ ORIENTATION IS NOT A PREFERENCE — HIGHEST STRIKE AT THE TOP, matching §STRUCTURE and the
   * price chart beside it. The owner reported the inverted case on 2026-08-10: two adjacent panels
   * disagreeing about which way price goes reads, at a glance, as calls sitting UNDER the market.
   * ⚠ The rows stay ASCENDING in data and are reversed only for DISPLAY — reversing the data would
   * silently mirror every derived placement, which is the half-fix that reads correct and is not.
   *
   * ⛔⛔ THE FETCH GOES THROUGH `api.ts`, AND THAT IS A CURE, NOT A TIDY-UP.
   * This pane was parked calling a bare `fetch("/api/dealer/ladder")`, which in the browser resolves
   * to the PAGES origin. pages.dev answers `/api/*` with **200 and the SPA shell** — the pane would
   * have rendered its fault box saying "Unexpected token '<'", blaming a worker that was serving
   * correctly. ⇒ *A 200 FROM THE WRONG ORIGIN IS THE MOST EXPENSIVE KIND OF SUCCESS* (api.ts).
   */
  import { onMount, untrack } from "svelte";
  import {
    fetchDealerLadder, fetchStrikeExpiryTyped, fetchStrikeExpirySum7d, FeedError, formatAge, subscribeServedVenueState,
    type StrikeExpiryTypedResponse, type StrikeExpirySum7dResponse,
    type DealerLadderResponse, type InstrumentVerdict, type VenueState,
  } from "./api";
  import {
    LABEL_FONT_MICRO, LABEL_FONT_PRIMARY, LABEL_FONT_SECONDARY, LABEL_MIN_GAP_PX,
    measuredSvgWidth, renderedPxToSvgUnits, resolveDealerLabelBaselines, type DealerLabelCandidate,
  } from "./layout";
  import type { LiveMarker } from "./livePriceMarker";
  import {
    ageVerdict, clampScrub, comparisonNote, coverageNote, dteLabel, glyphMark, rowDeltaKind,
    gex2BlockedReason, scrubFloorMs, spotProvenanceNote, spotHead, gex2SpotRule, gex2LiveRule,
    type Gex2Feed,
  } from "./gex2";
  import GexGridView from "./GexGridView.svelte";
  import { gexGridModel, sum7dModel } from "./gexGrid";
  import PanelFreshness from "./PanelFreshness.svelte";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  import { keptPayloadAfterFailure, lastGoodPayloadAfterResponse } from "./dataAge";

  /**
   * ⚠ A FALLBACK, NOT THE BUDGET. ar serves `budget_s` (the ruled `LADDER_BUDGET_S` 390) on every
   * ladder payload, and the served value wins. This is used only if a payload arrives without one —
   * a component-local number that could outvote the route would be a second budget for one
   * instrument, and one instrument gets one budget.
   */
  interface Props {
    defaultView?: "GRID" | "0DTE";
    budgetS?: number;
    livePrice: LiveMarker;
    dealerReport?: InstrumentVerdict | null;
  }
  let {
    budgetS = 390, livePrice, dealerReport = null, defaultView = "GRID",
  }: Props = $props();

  // A choice belongs to this mount; until one is made, the caller's default wins.
  let viewPreference = $state<"GRID" | "0DTE" | null>(null);
  let selectedView = $derived(viewPreference ?? defaultView);
  const VIEW_PREFS_KEY = "spx-hub.gex2-view.v1";
  type ViewChoice = "GRID" | "0DTE";
  type StoredViews = { app?: ViewChoice; focus?: ViewChoice };
  let panelElement = $state<HTMLElement | null>(null);
  let viewMount: "app" | "focus" = "app";
  let viewPrefsError = $state<string | null>(null);
  function parseStoredViews(raw: string | null): StoredViews {
    if (raw === null) return {};
    const stored: unknown = JSON.parse(raw);
    if (stored === null || typeof stored !== "object" || Array.isArray(stored)) throw new Error("invalid view preference record");
    const views = stored as Record<string, unknown>;
    for (const key of ["app", "focus"]) {
      if (views[key] !== undefined && views[key] !== "GRID" && views[key] !== "0DTE") throw new Error(`invalid ${key} view preference`);
    }
    return { ...(views.app ? { app: views.app as ViewChoice } : {}), ...(views.focus ? { focus: views.focus as ViewChoice } : {}) };
  }
  function viewPreferenceFailure(e: unknown) {
    viewPrefsError = e instanceof Error ? e.message : String(e);
    console.error(`[GEX GRID] VIEW PREFS ERROR: ${viewPrefsError}`);
  }
  function restoreViewPreference() {
    if (typeof window === "undefined") return;
    viewMount = panelElement?.closest("[data-focus-scope]") ? "focus" : "app";
    try {
      const stored = parseStoredViews(window.localStorage.getItem(VIEW_PREFS_KEY));
      viewPreference = stored[viewMount] ?? null;
      viewPrefsError = null;
    } catch (e) { viewPreferenceFailure(e); }
  }
  function chooseView(view: ViewChoice) {
    if (view === "GRID" && scrubTs !== null) {
      // Clear the historical spot immediately; only the unscrubbed response may crop GRID.
      feed = null;
      goLive();
    }
    viewPreference = view;
    if (typeof window === "undefined") return;
    try {
      // Read the latest peer mount on every write so one workspace cannot overwrite the other.
      const raw = window.localStorage.getItem(VIEW_PREFS_KEY);
      let stored: StoredViews;
      try { stored = parseStoredViews(raw); }
      catch { stored = {}; } // A deliberate choice repairs the corrupt record.
      window.localStorage.setItem(VIEW_PREFS_KEY, JSON.stringify({ ...stored, [viewMount]: view }));
      viewPrefsError = null;
    } catch (e) { viewPreferenceFailure(e); }
  }

  let gridBody = $state<StrikeExpiryTypedResponse | null>(null);
  let gridAgeBody = $state<StrikeExpiryTypedResponse | null>(null);
  let gridFetchedAtMs = $state<number | null>(null);
  let gridRequestKey = $state<string | null>(null);
  let gridAgeRequestKey = $state<string | null>(null);
  let gridStatus = $state(200);
  let gridError = $state<string | null>(null);
  let gridGeneration = 0;
  let gridLoaded = $state(false);
  // ORDER_HUD_UW_FACE_1_BOX2 Part C: the 7-day sum loads and refuses apart from the grid.
  let sumBody = $state<StrikeExpirySum7dResponse | null>(null);
  let sumAgeBody = $state<StrikeExpirySum7dResponse | null>(null);
  let sumFetchedAtMs = $state<number | null>(null);
  let sumRequestKey = $state<string | null>(null);
  let sumAgeRequestKey = $state<string | null>(null);
  let sumStatus = $state(200);
  let sumError = $state<string | null>(null);
  let sumGeneration = 0;
  let sumLoaded = $state(false);
  let lastSumLog: string | null = null;
  let mounted = $state(false);
  let lastGridLog: string | null = null;
  // A refusal draws no grid and logs an error; a drawn but degraded board (stale, hatched columns) warns.
  function logGridState(reason: string | null, level: "error" | "warn" = "error") {
    if (panelElement?.closest(".cockpit-hidden, [hidden]")) return;
    if (reason !== lastGridLog && reason !== null) console[level](`[GEX GRID] ${reason}`);
    lastGridLog = reason;
  }

  let feed = $state<Gex2Feed | null>(null);
  let feedFetchedAtMs = $state<number | null>(null);
  let feedRequestKey = $state<string | null>(null);
  let feedLoaded = $state(false);
  let error = $state<string | null>(null);
  /** Scrubber position: null = LIVE. A number is epoch-ms selecting the snapshot at-or-before. */
  let scrubTs = $state<number | null>(null);
  let nowMs = $state<number>(Date.now());
  let relayedVenueState = $state<VenueState | null>(null);

  let venueClosed = $derived(
    dealerReport?.venue_state
      ? dealerReport?.venue_state?.verdict === "CLOSED" ? dealerReport.venue_state : null
      : feed?.venue_state
        ? feed?.venue_state?.verdict === "CLOSED" ? feed.venue_state : null
        : relayedVenueState?.verdict === "CLOSED" ? relayedVenueState : null,
  );
  // R5 (review r3 P2, 01a1585d): a same-key retained ladder that is itself drawable stays drawn beside FEED ERROR;
  // the error blocks the pane only when no drawable payload is held.
  let feedDrawable = $derived(feed !== null && gex2BlockedReason(feed, null) === null);
  let feedBlocked = $derived(venueClosed ? null : gex2BlockedReason(feed, feedDrawable ? null : error));
  let provenance = $derived(spotProvenanceNote(feed));
  let coverage = $derived(coverageNote(feed));
  let comparison = $derived(comparisonNote(feed));
  let age = $derived(ageVerdict(feed, budgetS));
  let floorMs = $derived(scrubFloorMs(feed));
  let infoOpen = $state(false);
  let freshnessTitle = $derived(age?.kind === "stale"
    ? `GEX-2 snapshot age ${age.ageS}s · budget ${age.budgetS}s`
    : "GEX-2 live");
  /** ⚠ The chip is checked against the PAYLOAD's expiry, never asserted by the heading. */
  let dte = $derived(dteLabel(feed, new Date(nowMs).toISOString().slice(0, 10)));

  /** ASCENDING in data (the contract), reversed only to draw. */
  let shown = $derived(feed?.ok && Array.isArray(feed.strikes) ? feed.strikes : []);
  let displayRows = $derived([...shown].reverse());
  let maxAbs = $derived(shown.reduce((m, r) => Math.max(m, Math.abs(r.net)), 0) || 1);
  let staleRows = $derived(shown.filter((r) => r.stale === true).length);

  const W = 320, ROW_H = 13, PAD_T = 8, PAD_B = 10, GUTTER = 46;
  const ZERO = GUTTER + (W - GUTTER) * 0.52;
  let renderedSvgWidth = $state(0);
  // The explicit SVG height caps its uniform scale at 1 even when the rail is wider than W.
  let measuredWidth = $derived(measuredSvgWidth(renderedSvgWidth, W, true));
  let primaryFontSvg = $derived(measuredWidth === null ? 0 : renderedPxToSvgUnits(LABEL_FONT_PRIMARY, W, measuredWidth));
  let secondaryFontSvg = $derived(measuredWidth === null ? 0 : renderedPxToSvgUnits(LABEL_FONT_SECONDARY, W, measuredWidth));
  let microFontSvg = $derived(measuredWidth === null ? 0 : renderedPxToSvgUnits(LABEL_FONT_MICRO, W, measuredWidth));
  let labelGapSvg = $derived(measuredWidth === null ? 0 : renderedPxToSvgUnits(LABEL_MIN_GAP_PX, W, measuredWidth));
  let blocked = $derived(measuredWidth === null
    ? `§GEX-2 · RAIL UNMEASURED — width ${renderedSvgWidth}, no draw`
    : selectedView === "GRID" ? null : feedBlocked);
  let H = $derived(PAD_T + displayRows.length * ROW_H + PAD_B);
  const y = (i: number) => PAD_T + i * ROW_H;
  const barW = (net: number) => (Math.abs(net) / maxAbs) * ((W - GUTTER) * 0.4);

  /**
   * ⛔ ONE CALL, TWO OUTPUTS (as's FIX-FIRST, au concurring). The head and the line must be the SAME
   * number — R808③ — so they are no longer two independent reads of `feed`. Nulling the line's
   * argument is not expressible here any more; the only way to blank the line is to blank the head
   * with it, which the owner would see immediately.
   */
  let spotRule = $derived(gex2SpotRule(feed, displayRows.map((r) => r.strike), { padT: PAD_T, rowH: ROW_H }));
  let head = $derived(spotRule.head);

  /**
   * ⚠ THE THREE DECLARATIONS ABOVE SIT HERE, NOT WITH THE OTHER `$derived`s — GK-as's 2nd finding.
   * This call reads `displayRows`, `PAD_T` and `ROW_H`, all declared below that block. `$derived`
   * is lazy so it RAN correctly and `vite build` was perfectly happy, but `svelte-check` went from
   * ** 191 files 0 errors ** on main to ** 6 errors ** on my cut, and it is a gate.
   * ⇒ ***A LAZY EVALUATION ORDER IS NOT A DECLARATION ORDER, AND ONLY ONE OF THEM IS GATED.***
   * ⛔ AND THE REASON I MISSED IT: `svelte-check` lives in `check:all`, NOT in `build:all`. Nine
   * green gates said nothing about it. **Run both chains before calling a cut proven.**
   */

  /**
   * R805① — SPOT, DRAWN. The owner: *"the spot price isn't on the 0DTE exposure panel."* Exact.
   * This pane already CENTRED its viewport on spot and printed it at the head, and never drew it
   * on the ladder — so the one number the bars are positioned against was the one number you
   * could not locate among them.
   *
   * ⛔ NO NEW SOURCE. `feed.spot` is the ladder's OWN spot — the value `centreOnSpot` scrolls to and
   * `spotHead` prints, with `spotProvenanceNote` already disclosing where it came from. R805①
   * proposed drawing `dealer/levels.spot`; that would put a SECOND spot on a panel whose own
   * §R424(3) note makes provenance the point, and the two need not agree.
   * ⇒ ***THE LINE AND THE HEAD MUST BE THE SAME NUMBER, OR THE PANEL ARGUES WITH ITSELF.***
   */
  let spotPlacement = $derived(spotRule.placement);
  let livePlacement = $derived(gex2LiveRule(
    livePrice,
    displayRows.map((r) => r.strike),
    { padT: PAD_T, rowH: ROW_H },
  ));
  let priceLabelCandidates = $derived.by((): DealerLabelCandidate[] => {
    const candidates: DealerLabelCandidate[] = [];
    if (spotPlacement.kind !== "absent") candidates.push({ id: "snapshot", preferredY: spotPlacement.y - 3 });
    if (livePlacement.kind !== "absent") candidates.push({ id: "live", preferredY: livePlacement.y - 3 });
    return candidates;
  });
  let priceLabelBaselines = $derived(measuredWidth === null
    ? new Map<string, number>()
    : resolveDealerLabelBaselines(
        priceLabelCandidates,
        { minY: primaryFontSvg, maxY: Math.max(primaryFontSvg, H - 4) },
        labelGapSvg,
      ));

  const hhmm = (ms: number) =>
    new Date(ms).toISOString().slice(11, 16) + "Z";

  let ladderEl = $state<HTMLDivElement | null>(null);
  let requestGeneration = 0;
  /**
   * Open the bounded viewport CENTRED ON SPOT rather than at the top.
   *
   * ⚠ The top of the ladder is the FURTHEST strike from the market. A scroll box that opens at its
   * natural origin would show the reader the emptiest end of the data first — the same 43 blank rows
   * that made the unbounded pane read as broken, just in a smaller frame. Bounding the height
   * without choosing the origin moves the defect; it does not cure it.
   */
  function centreOnSpot() {
    const el = ladderEl;
    if (!el || displayRows.length === 0) return;
    const spot = feed?.spot;
    if (typeof spot !== "number" || !Number.isFinite(spot)) return;
    let best = 0;
    for (let i = 1; i < displayRows.length; i++) {
      if (Math.abs(displayRows[i].strike - spot) < Math.abs(displayRows[best].strike - spot)) best = i;
    }
    el.scrollTop = Math.max(0, y(best) - el.clientHeight / 2);
  }
  $effect(() => {
    // Re-centre whenever the drawn set changes — a scrub to another snapshot moves spot with it.
    if (ladderEl && displayRows.length) centreOnSpot();
  });

  async function loadGrid() {
    if (selectedView !== "GRID") return;
    const mine = ++gridGeneration;
    const requestKey = "/api/uw/strike-expiry?root=SPX&source=typed";
    try {
      const response = await fetchStrikeExpiryTyped();
      if (mine !== gridGeneration) return;
      gridBody = response.body;
      gridRequestKey = requestKey;
      gridStatus = response.status;
      gridError = null;
      gridAgeBody = lastGoodPayloadAfterResponse(
        gridAgeBody, gridAgeRequestKey, requestKey, response.body, response.status,
      );
      if (response.status >= 200 && response.status < 300) {
        gridFetchedAtMs = Date.now();
        gridAgeRequestKey = requestKey;
      } else {
        if (gridAgeBody === null) {
          gridFetchedAtMs = null;
          gridAgeRequestKey = null;
        }
      }
      const parsed = gexGridModel(response.body, "gex", response.status);
      const badColumns = parsed.ok ? parsed.columns.filter(c => c.hatched) : [];
      // The board's STALE is part of the state (no age in it, so the log stays once per change).
      logGridState(!parsed.ok ? parsed.refusal : [
        ...badColumns.map(c => `${c.expiry} · ${c.state} · ${c.detail ?? ""}`),
        ...(parsed.stale ? ["BOARD STALE"] : []),
      ].join("; ") || null, parsed.ok ? "warn" : "error");
    } catch (e) {
      if (mine !== gridGeneration) return;
      gridBody = keptPayloadAfterFailure(gridBody, gridRequestKey, requestKey);
      gridAgeBody = keptPayloadAfterFailure(gridAgeBody, gridAgeRequestKey, requestKey);
      if (gridBody === null) {
        gridRequestKey = null;
      }
      if (gridAgeBody === null) {
        gridFetchedAtMs = null;
        gridAgeRequestKey = null;
      }
      gridError = e instanceof FeedError && e.status === 400 ? "GRID UNSERVED" : e instanceof Error ? e.message : String(e);
      logGridState(gridError);
    } finally {
      if (mine === gridGeneration) gridLoaded = true;
    }
  }

  async function loadSum() {
    if (selectedView !== "GRID") return;
    const mine = ++sumGeneration;
    const requestKey = "/api/uw/strike-expiry?root=SPX&source=typed&window=7d";
    let reason: string | null = null;
    let level: "error" | "warn" = "error";
    try {
      const response = await fetchStrikeExpirySum7d();
      if (mine !== sumGeneration) return;
      sumBody = response.body;
      sumRequestKey = requestKey;
      sumStatus = response.status;
      sumError = null;
      sumAgeBody = lastGoodPayloadAfterResponse(
        sumAgeBody, sumAgeRequestKey, requestKey, response.body, response.status,
      );
      if (response.status >= 200 && response.status < 300) {
        sumFetchedAtMs = Date.now();
        sumAgeRequestKey = requestKey;
      } else {
        if (sumAgeBody === null) {
          sumFetchedAtMs = null;
          sumAgeRequestKey = null;
        }
      }
      const parsed = sum7dModel(response.body, "gex", response.status);
      // A refusal is an error; a drawn but degraded sum (its own STALE, PARTIAL) warns, once per change.
      level = parsed.ok ? "warn" : "error";
      reason = !parsed.ok ? parsed.refusal
        : [parsed.stale ? "STALE" : "", parsed.complete ? "" : "PARTIAL"].filter(Boolean).join(" · ") || null;
    } catch (e) {
      if (mine !== sumGeneration) return;
      sumBody = keptPayloadAfterFailure(sumBody, sumRequestKey, requestKey);
      sumAgeBody = keptPayloadAfterFailure(sumAgeBody, sumAgeRequestKey, requestKey);
      if (sumBody === null) {
        sumRequestKey = null;
      }
      if (sumAgeBody === null) {
        sumFetchedAtMs = null;
        sumAgeRequestKey = null;
      }
      sumError = e instanceof FeedError && e.status === 400 ? "SUM UNSERVED" : e instanceof Error ? e.message : String(e);
      reason = sumError;
    } finally {
      if (mine === sumGeneration) sumLoaded = true;
    }
    if (panelElement?.closest(".cockpit-hidden, [hidden]")) return;
    if (reason !== lastSumLog && reason !== null) console[level](`[GEX GRID] Σ7D ${reason}`);
    lastSumLog = reason;
  }

  async function load() {
    const mine = ++requestGeneration;
    const at = scrubTs === null ? null : new Date(scrubTs).toISOString();
    const requestKey = at === null ? "/api/dealer/ladder" : `/api/dealer/ladder?ts=${encodeURIComponent(at)}`;
    try {
      nowMs = Date.now();
      const j = (await fetchDealerLadder(at)) as DealerLadderResponse;
      if (mine !== requestGeneration) return;
      feed = j as Gex2Feed;
      feedFetchedAtMs = Date.now();
      feedRequestKey = requestKey;
      error = j.ok ? null : (j.reason ?? "the ladder route returned not-ok");
    } catch (e) {
      // ⛔ NO FALLBACK to the pooled §STRUCTURE ladder. It is a DIFFERENT QUANTITY (all-expiry) and
      // substituting it under this pane's 0DTE heading is the exact lie the chart shed today.
      if (mine === requestGeneration) {
        feed = keptPayloadAfterFailure(feed, feedRequestKey, requestKey);
        if (feed === null) {
          feedFetchedAtMs = null;
          feedRequestKey = null;
        }
        error = e instanceof Error ? e.message : String(e);
        error += refusedRowsFault(e);
      }
    } finally {
      if (mine === requestGeneration) feedLoaded = true;
    }
  }

  /**
   * ⚠ THE CONTROL MAY NOT ASK FOR A REFUSAL. Below `scrub_floor_ts` ar's route answers 400 — every
   * snapshot there is a 2031-12-19 LEAP. A slider that can reach a guaranteed 400 teaches the reader
   * the pane is broken, so the ask is clamped before it is sent, not after it fails.
   */
  function onScrub(e: Event) {
    const raw = Number((e.currentTarget as HTMLInputElement).value);
    scrubTs = clampScrub(raw, floorMs, Date.now());
    void load();
  }
  function goLive() { scrubTs = null; void load(); }

  onMount(() => {
    restoreViewPreference();
    const unsubscribeServedVenueState = subscribeServedVenueState((state) => {
      relayedVenueState = state;
    });
    void load();
    mounted = true;
    const t = setInterval(load, 60_000);
    return () => {
      clearInterval(t);
      mounted = false;
      gridGeneration++;
      sumGeneration++;
      unsubscribeServedVenueState();
    };
  });
  $effect(() => {
    if (!mounted || selectedView !== "GRID") return;
    untrack(() => { void loadGrid(); void loadSum(); });
    const timer = setInterval(() => { void loadGrid(); void loadSum(); }, 60_000);
    return () => { clearInterval(timer); gridGeneration++; sumGeneration++; };
  });
  // R1/R3: retain a named malformed alert instead of dropping the map.
  let refusedRows = $derived.by(() => {
    if (feed === null) return { state: "NO_BODY" as const };
    try { return readRefusedRows(feed); }
    catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error(`[Gex2Panel] /api/dealer/ladder ${message}`);
      return { state: "MALFORMED" as const, message };
    }
  });
</script>

<section class="gex2" aria-label="live 0DTE dealer exposure" bind:this={panelElement}
  style={measuredWidth === null ? undefined : `--svg-label-font-primary:${primaryFontSvg}px;--svg-label-font-secondary:${secondaryFontSvg}px;--svg-label-font-micro:${microFontSvg}px`}>
  <div class="view-control" role="group" aria-label="VIEW">
    <button type="button" aria-pressed={selectedView === "GRID"} onclick={() => chooseView("GRID")}>GRID</button>
    <button type="button" aria-pressed={selectedView === "0DTE"} onclick={() => chooseView("0DTE")}>0DTE</button>
  </div>
  {#if viewPrefsError}<span class="chip warn" data-chart-alert role="status">VIEW PREFS ERROR</span>{/if}
  <AgePill payload={feed} fetchedAtMs={feedFetchedAtMs} door={feedRequestKey ?? "/api/dealer/ladder"} servedOnly />
  {#if error}<span class="chip warn" data-gex2-feed-error role="alert">FEED ERROR</span>{/if}
  {#if refusedRows.state === "REFUSED"}<span class="chip stale" data-refused-rows role="status" title={refusedRowsDetail("/api/dealer/ladder", refusedRows, "ladder")}>REFUSED {refusedRows.total} {refusedRows.total === 1 ? "ROW" : "ROWS"}</span>{/if}
  {#if refusedRows.state === "MALFORMED"}<span class="chip stale" data-refused-rows role="alert">{refusedRows.message}</span>{/if}
  {#if selectedView === "0DTE"}
  <header class="panel-id">
    <h3 class="railsec">GEX-2</h3>
    <span class="pill" class:wrong={!dte.honest}>{dte.text}</span>
    {#if feed?.ok}<span class="pill">{feed.expiry}</span>{/if}
    {#if feed?.ok && feed.ticker}<span class="pill">{feed.ticker}</span>{/if}
    <span class="hyp">HYP</span>
    <!-- PanelFreshness delegates CLOSED rendering to the shared VenueClosedBanner. -->
    <PanelFreshness label="GEX-2" venueState={venueClosed} stale={age?.kind === "stale"} ageTitle={freshnessTitle} />
    <PanelInfo id="gex2-info" label="GEX-2 information" open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
      {#if refusedRows.state === "REFUSED"}
        <p>/api/dealer/ladder</p>
        {#each refusedRows.items as item}<p>{item.field}:{item.reason} · {item.count} dropped before the ladder was drawn</p>{/each}
      {:else if refusedRows.state === "UNSERVED"}
        <p>refused rows · UNSERVED (worker before 8b72948f)</p>
      {:else if refusedRows.state === "MALFORMED"}
        <p>/api/dealer/ladder · {refusedRows.message}</p>
      {/if}
      <p>HYPOTHESIS · no study passed</p>
      {#if viewPrefsError}<p>VIEW PREFS ERROR · {viewPrefsError}</p>{/if}
      {#if venueClosed}<p>last session {venueClosed.last_close?.slice(0, 10) ?? "UNKNOWN"}</p>{/if}
      {#if venueClosed && error}<p><b>NO 0DTE LADDER DRAWN</b> — {error}</p>{/if}
      {#if staleRows > 0}<p>{staleRows}/{shown.length} rows stale</p>{/if}
      {#if scrubTs !== null}<p>HISTORY {hhmm(scrubTs)}</p>{/if}
      <div class="spothead">
        {#if head.kind === "served"}<span class="spotval">{head.text}</span>{:else}<span class="spotval unserved">—</span>{/if}
        <span class="spotmeta"><span class="spotlbl">SPX spot</span>
          {#if age?.kind === "unserved"}<span class="spotage unserved">SNAPSHOT AGE UNSERVED</span>
          {:else if age?.kind === "stale"}<span class="spotage stale">snapshot STALE {age.ageS}s / {age.budgetS}s</span>
          {:else if age?.kind === "live"}<span class="spotage">snapshot {age.ageS}s</span>{/if}
        </span>
      </div>
      {#if head.kind === "served"}<p>SPX spot {head.text}</p>{:else}<p>SPX spot — · {head.note}</p>{/if}
      {#if age?.kind === "unserved"}<p>SNAPSHOT AGE UNSERVED · {age.note}</p>{/if}
      {#if provenance}<p>{provenance}</p>{/if}
      {#if coverage}<p>{coverage}</p>{/if}
      {#if comparison}<p>{comparison}</p>{/if}
      {#if venueClosed}<p><b>CLOSED · the last session's exposure · no minute changes while the venue is shut</b></p>{:else}<p><b>▲add ▼pull · no change · blank = no prior snapshot</b></p>{/if}
      <p>+GEX / -GEX: the sign of the vendor's net exposure; no behaviour is claimed</p>
      {#if floorMs !== null}<p>history floor {hhmm(floorMs)} · {feed?.scrub_floor_basis ?? "no 0DTE history exists before this instant; earlier snapshots are 2031-12-19 LEAPs"}</p>{/if}
    </PanelInfo>
  </header>

  {/if}

  <!--
    ⚠ THE SPOT'S PROVENANCE IS DISCLOSED, NOT ASSUMED (R424(3), R422 open). The whole ladder is
    CENTRED on spot, and three hands proved today that a stale spot does not misplace a line — it
    narrows the candidate set and a ruled function then picks a different strike.
  -->
  <!--
    ⛔ R591(3) — SPOT AT THE HEAD, LARGE. It used to live mid-prose ("spot 7645 · …") and the owner
    could not find the one number the whole ladder is centred on. It is now the first thing read.

    ⚠ THE AGE BESIDE IT IS LABELLED `snapshot`, AND THAT WORD IS LOAD-BEARING. `age_s` is measured
    server-side on the OLDEST NEAR-SPOT ROW — it is the snapshot's freshness, NOT this tick's. Set
    flush against a big price with no label it would read as "this price is 37s old", a claim the
    payload never makes. A number borrowed from a neighbouring field and shown under a new name is
    the quietest way to invent a measurement.
    ⛔ AND THE AGE LIVES HERE RATHER THAN IN THE CHIP ROW because it now sits with the number it
    qualifies — rendered from the SAME `ageVerdict`, so there is exactly one answer on screen.
  -->
  <div class="laddermeasure" bind:clientWidth={renderedSvgWidth}>
  {#if blocked}
    {#if measuredWidth === null}
    <span class="chip warn" data-chart-alert>{blocked}</span>
    {:else}
    <!-- Never a blank pane. The estate's oldest law, applied to its newest surface. -->
    <div class="fault" role="status"><b>NO 0DTE LADDER DRAWN</b><small>{blocked}</small></div>
    {/if}
  {:else if selectedView === "GRID"}
    <GexGridView {gridLoaded} {feedLoaded} venueClosed={venueClosed !== null}
      {gridAgeBody} {gridBody} {gridFetchedAtMs} {gridStatus} {gridError}
      {sumLoaded} {sumAgeBody} {sumBody} {sumFetchedAtMs} {sumStatus} {sumError}
      {feed} {microFontSvg} {budgetS} spotError={error} preferenceError={viewPrefsError} />
  {:else if displayRows.length}
  <!--
    ⛔⛔ BOUNDED, AND THIS IS AN EYE FINDING NO ARM COULD HAVE MADE.
    Rendered against ar's REAL payload the pane came out **1281px tall** — 81 near-spot rows at 13px —
    and **43 of those 81 rows drew nothing at all**: the route slices ±200 points from spot while
    tonight's exposure lives inside ±90. Every arm was green and every contract matched; the pane was
    still a 1,281-pixel column that is mostly blank labels, in a rail beside a 1,000px chart.
    ⇒ ⭐ ***A CONTRACT MATCH IS NOT A LAYOUT. The suite proved the pane READ the data correctly and had
    no instrument for what that data DOES to the page.***
    ⚠ THE CURE HIDES NOTHING. Every served strike is still in the DOM and still reachable by scroll —
    a zero row is a real measurement ("no exposure here"), not padding to be dropped. Only the
    VIEWPORT is bounded, and it opens centred on spot so the signal is what you see first.
  -->
  <div class="ladderview" bind:this={ladderEl} data-dealer-closed-ladder={venueClosed ? "LAST_COMPLETE_SESSION" : undefined}>
    <!--
      ⛔⛔ `viewBox` + width:100% — AND THIS IS THE THIRD DEFECT THE EYE FOUND THAT EVERY ARM MISSED.
      The SVG was a FIXED `width={W}` = 320px inside a rail that measures 296px. Measured in the
      browser, 2026-08-22:
      ```
        .ladderview  x=1124  right=1420  w=296
        svg          x=1124  right=1444  w=320    <- 24px wider than its own container
        first glyph  x=1432 .. 1436               <- 12px PAST the visible edge · clipped: true
      ```
      ⇒ ***THE ADD/PULL COLUMN HAS NEVER BEEN VISIBLE TO THE OWNER.*** It rendered, it was in the DOM,
      arms could count it — and it sat outside the frame. `overflow-y:auto` makes overflow-x `auto`
      too, so it was reachable only by scrolling sideways in a panel nobody would think to scroll.
      ⚠ THIS IS WHY MY OWN "glyphs 0" READINGS WERE RIGHT FOR THE WRONG REASON: post-close there were
        genuinely no add/pull marks, so I concluded the feature was merely unexercised. **A true
        reading of a broken instrument still reads true.**
      ⇒ `viewBox` keeps every x/y computation in the 320-unit space while the element scales to
        whatever the rail actually is — so this cannot recur when the rail width changes again.
    -->
    <svg viewBox="0 0 {W} {H}" width="100%" height={H} preserveAspectRatio="xMidYMin meet"
         role="img" aria-label="0DTE net exposure by strike">
      {#each displayRows as r, i}
        {@const k = rowDeltaKind(r.delta, venueClosed !== null)}
        <text class="strike" class:rowstale={r.stale} x={GUTTER - 6} y={y(i) + 9} text-anchor="end">{r.strike}</text>
        <!--
          ⛔ A STALE ROW IS DIMMED, NOT HIDDEN AND NOT DRAWN AS FLAT. Its exposure is REAL; only its
          freshness is not. ar computes `stale` against the snapshot's OWN newest vendor time, so a
          03:00Z read is not "all stale" merely because the market is shut.
        -->
        <rect class={r.net >= 0 ? "pos" : "neg"} class:rowstale={r.stale}
              x={r.net >= 0 ? ZERO : ZERO - barW(r.net)} y={y(i) + 2}
              width={barW(r.net)} height={ROW_H - 4}>
          {#if r.stale}<title>vendor time {r.time} — older than this snapshot's newest</title>{/if}
        </rect>
        <!--
          ADD / PULL / FLAT from the MINUTE DELTA. THREE marks for three states, and ONE deliberate
          blank.

          ⛔⛔ `flat` HAD NO BRANCH HERE AND THAT WAS NEVER A DECISION — IT WAS A HOLE STANDING
          BEHIND A CORRECT SENTENCE. The comment that used to sit here justified `unknown` drawing
          nothing (a null delta is no prior snapshot to diff, so a mark would assert a measurement
          nobody took). **That reasoning is right, and it is ONLY about `unknown`.** `flat` drew
          nothing because the `{#if}` had no arm for it.
          ⇒ ⭐⭐ ***A COMMENT JUSTIFYING ONE OMITTED BRANCH SILENTLY LICENSES A SECOND OMISSION
            NOBODY DECIDED*** (GK-tt, `bh=dbb961df`) — a later reader sees reasoned justification
            beside an unreasoned gap and takes both as intentional.
          ⚠ AND IT IS NOT A CORNER CASE. Measured against Friday's replay, session hour 16:00-16:57Z
            sampled every 3 min: **5 of 20 minutes had ZERO moving strikes — 25%.** One session
            minute in four the whole column was blank, and a blank column is indistinguishable from
            "no prior snapshot", "the feed died", and "never built".
          ⇒ `flat` now draws a quiet dash at `--faint`; `unknown` KEEPS its deliberate blank. The
            distinction `deltaKind` has always made in the pure layer finally survives to the pixel.
        -->
        {@const mark = glyphMark(k)}
        {#if mark}<text class="glyph {k}" x={W - 8} y={y(i) + 9} text-anchor="end">{mark}</text>{/if}
      {/each}

      <!--
        SPOT — drawn LAST so it sits above the bars, and drawn THIN so it never outweighs them.
        The treatment is `DealerRailPanel`'s, unchanged on purpose: one quantity gets one visual
        language across both ladders, or the reader has to learn the panel before reading the chart.

        ⛔ THE LABEL IS NOT DECORATION — identity is never colour-alone, so the line carries its own
        price. It is anchored in the GUTTER (the sibling's GAMMA FLIP idiom) rather than at the right
        edge, because this pane's right column is the add/pull glyph strip and a right-anchored label
        would land on top of it.
        ⚠ A CLAMPED SPOT SAYS SO. Pinning it to the honest edge without the word would claim a
        position on a ladder that does not contain it.
      -->
      {#if spotPlacement.kind === "at" || spotPlacement.kind === "clamped"}
        <line
          x1={GUTTER - 2} x2={W - 4} y1={spotPlacement.y} y2={spotPlacement.y}
          class:spotrule={spotPlacement.kind === "at"}
          class:clampedmark={spotPlacement.kind === "clamped"}
        />
        <text
          x={GUTTER + 2}
          y={priceLabelBaselines.get("snapshot") ?? (spotPlacement.y - 3)}
          class="lvl spotrulelbl"
          class:clampedlbl={spotPlacement.kind === "clamped"}
        >{spotPlacement.kind === "clamped"
            ? `CLAMPED ${spotPlacement.edge.toUpperCase()} · `
            : ""}SPOT {feed?.spot}</text>
      {/if}
      {#if livePrice.kind === "draw" && (livePlacement.kind === "at" || livePlacement.kind === "clamped")}
        <line
          x1={GUTTER - 2} x2={W - 4} y1={livePlacement.y} y2={livePlacement.y}
          class="liverule"
          class:clampedmark={livePlacement.kind === "clamped"}
        />
        <text
          x={W - 4}
          y={priceLabelBaselines.get("live") ?? (livePlacement.y - 3)}
          class="lvl liverulelbl"
          class:clampedlbl={livePlacement.kind === "clamped"}
          text-anchor="end"
          data-primary-price="LIVE"
        >{livePlacement.kind === "clamped"
            ? `CLAMPED ${livePlacement.edge.toUpperCase()} · `
            : ""}LIVE {livePrice.price}</text>
      {/if}
    </svg>
  </div>

    <!--
      ⛔⛔ DRAWABLE BUT UNPLACEABLE — THE THIRD STATE, TAKEN FREE FROM `DealerRailPanel`'s SCAR.
      That panel shipped a PERFECTLY GOOD live price that rendered NOTHING: the decision said
      "draw", the placement returned null, and no note existed to replace the line. Measured there
      as `absent-note 0 · live-line 0` — the exact law-1 silence this state exists to prevent.
      ⇒ *** "THE VALUE IS GOOD" AND "THE VALUE CAN BE SHOWN" ARE DIFFERENT QUESTIONS. ***
      The head keeps its own UNSERVED wording for a spot that never arrived, so this note fires only
      when a REAL spot could not be placed — otherwise the pane would say absent twice, differently.
    -->
    {#if !venueClosed && spotPlacement.kind === "absent" && typeof feed?.spot === "number"}
      <p class="spotunplaced" role="note">SPOT {feed.spot} NOT PLACED — {spotPlacement.reason}</p>
    {/if}
    {#if !venueClosed && livePrice.kind === "absent"}
      <p class="spotunplaced" role="note">LIVE PRICE ABSENT — {livePrice.reason}</p>
    {:else if !venueClosed && livePrice.kind !== "absent" && livePlacement.kind === "absent"}
      <p class="spotunplaced" role="note">LIVE {livePrice.price} NOT PLACED — {livePlacement.reason}</p>
    {/if}

  {:else if venueClosed}
    <div class="fault" role="status" data-dealer-closed-empty>
      <b>LAST SESSION UNREAD</b>
      <small>{error ?? feed?.reason ?? "no rowed dealer snapshot exists at or before the last close"}</small>
    </div>
  {:else}
    <!--
      ⛔ THE THIRD STATE, WHICH USED TO RENDER NOTHING AT ALL. `{#if blocked}` / `{:else if
      displayRows.length}` left `blocked === null` with zero rows unhandled, and the pane emitted an
      empty box — "the `{#if}` had no arm for it", the exact phrase already sitting in this
      component's glyph comment, one conditional up. A conditional over a state space must be TOTAL.
    -->
    <div class="fault" role="status">
      <b>NO SNAPSHOT AT THIS INSTANT</b>
      <small>nothing is blocked and no rows were served — scrub back toward LIVE, or press LIVE</small>
    </div>
  {/if}
  </div>

  <!--
    THE TIME SCRUBBER — OUTSIDE the ladder conditional, and that placement is the R591(2) cure.
    LIVE is a STATE, not the slider's maximum: the newest snapshot moves, so a control parked at max
    would pin the reader to a stale instant while the pane still said live. The LIVE button
    therefore clears the position rather than seeking to the end.

    ⛔⛔ IT USED TO SIT INSIDE `{:else if displayRows.length}`, so EVERY blocked state removed the
    slider and the LIVE button along with the ladder — the owner scrubbed into a gap and had no way
    back without reloading. The ROUTE already ships `scrub_floor_ts` on its refusal precisely so
    this control can outlive the ladder (HUDCHAT-3, `21d49077`), and `scrubFloorMs` reads it without
    gating on `feed.ok` — so the remedy arrived intact and the markup declined to render it.
    ⇒ ***A CURE DELIVERED TO A CONSUMER THAT CANNOT REACH IT IS NOT A CURE.***
  -->
  {#if selectedView === "0DTE" && floorMs !== null}
    <div class="scrubber">
      <label class="srlabel" for="gex2-scrub">snapshot</label>
      <input id="gex2-scrub" type="range" min={floorMs} max={nowMs} step={60_000}
             value={scrubTs ?? nowMs} oninput={onScrub}
             aria-label="scrub to an earlier snapshot" />
      <button type="button" class="livebtn" class:on={scrubTs === null} onclick={goLive}>LIVE</button>
    </div>
  {/if}
</section>

<style>
  .view-control { display: inline-flex; align-self: flex-start; flex-wrap: wrap; gap: 2px; border: 1px solid var(--line); border-radius: var(--radius-pill); background: var(--chip); padding: 2px; }
  .view-control button { padding: 2px 6px; border: 0; color: var(--muted); background: transparent; font: 10px var(--font-num); cursor: pointer; }
  .view-control button[aria-pressed="true"] { color: var(--ink); background: var(--surface); }
  .view-control button:focus-visible { outline: 1px solid var(--ink); }

  .gex2 { display: flex; flex-direction: column; gap: 6px; }
  .panel-id { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .panel-id .pill { display: inline-flex; align-items: center; border: 1px solid var(--line); border-radius: var(--radius-pill); padding: 2px 7px; background: var(--chip); color: var(--muted); font-size: 10px; line-height: 1; }
  .panel-id .hyp { color: var(--faint); }
  .railsec { font: 600 11px var(--font-ui); letter-spacing: .08em; color: var(--ink); margin: 0; }
  .railsec small { font-weight: 400; color: var(--muted); letter-spacing: 0; }
  .chips { display: flex; gap: 6px; flex-wrap: wrap; }
  .chip { font: 10px var(--font-num); color: var(--muted); border: 1px solid var(--line);
          border-radius: var(--radius-chip); padding: 1px 6px; }
  .chip.dte { color: var(--ink); border-color: var(--accent); }
  .chip.dte.wrong { color: var(--stale-ink); background: var(--stale-soft); border-color: var(--stale); }
  .chip.stale { color: var(--stale-ink); background: var(--stale-soft); border-color: var(--stale); }
  /* ORDER_HUD_FACE_LEGIBILITY_1 R2: VIEW PREFS ERROR and the blocked chip use .chip.warn, which this scoped style
     never defined, so both drew neutral. Copied from SessionCvdPane.svelte:1760. */
  .chip.warn { background: var(--stale-soft); border: 1px solid var(--stale); color: var(--stale-ink); }
  /* An unserved age is our own blind spot, not the feed's fault — dashed, not amber-filled. */
  .chip.unserved { color: var(--stale-ink); border-style: dashed; border-color: var(--stale); }
  .chip.scrub { color: var(--flip); border-color: var(--flip); }
  .prov { margin: 0; font: 10px var(--font-num); color: var(--faint); }
  .prov.warn { color: var(--stale-ink); }
  .prov.floor { color: var(--faint); }
  .fault { border: 1px dashed var(--stale); background: var(--stale-soft); color: var(--stale-ink);
           border-radius: var(--radius-chip); padding: 6px 8px; display: flex; flex-direction: column; gap: 2px; }
  .fault b { font: 600 10px var(--font-ui); letter-spacing: .06em; }
  .fault small { font-size: 10px; }
  text.strike { font: 9px var(--font-num); fill: var(--faint); }
  text.strike.rowstale { fill: var(--stale-ink); }
  rect.pos { fill: var(--up); } rect.neg { fill: var(--dn); }
  /* Dimmed, never hidden: the exposure is real, the freshness is not. */
  rect.rowstale { opacity: .35; }
  .glyph { font-size: var(--svg-label-font-micro); } .glyph.add, .glyph.pull { fill: var(--ink); }
  /* Quiet on purpose: "measured, and it did not move" must not compete with "dealers acted".
     ⚠ BUT NOT BELOW THE CONTRAST FLOOR. `--faint` measured 4.27:1 on the light theme — under WCAG
     AA's 4.5:1 for 9px text — so the mark meant to END an ambiguity would itself have been hard to
     see, in exactly one theme. `--muted` is the next step up and still recedes behind add/pull,
     which carry saturated hues. QUIET IS A CONTRAST BUDGET, NOT AN INVISIBILITY LICENCE. */
  .glyph.flat { fill: var(--muted); }

  /*
   * THE SPOT LINE — `DealerRailPanel`'s ratified treatment, copied VALUE FOR VALUE rather than
   * re-chosen. One quantity, one visual language across both ladders.
   * ⛔ THIN AND DASHED ON PURPOSE: the bars are the data, this is a reference mark. A heavier rule
   * would win the eye from the exposure it exists to locate.
   * Both themes come free — every colour here is a token, so `data-theme` moves them together and
   * nothing is hard-coded to one surface.
   */
  /*
   * ⚠ NAMED `spotrule*`, NOT `spot*`. This pane ALREADY owns `.spotlbl` for the header's "SPX spot"
   * caption (§THE SPOT HEAD). Reusing it put two rules on one class: the header's `font:` shorthand
   * silently overrode this mark's `font-size`, and this mark's `fill` leaked onto a header <span>.
   * ⇒ ***A CLASS NAME IS AN INTERFACE — sharing one between a caption and a chart mark couples two
   * things that must be free to move independently.***
   */
  .spotrule { stroke: var(--ink); stroke-width: 1; stroke-dasharray: 2 4; opacity: 0.75; }
  .liverule { stroke: var(--accent); stroke-width: 1.5; stroke-dasharray: 7 3; }
  .clampedmark { stroke: var(--stale); stroke-width: 2; }
  .lvl { font-family: var(--font-num); font-size: var(--svg-label-font-secondary); letter-spacing: 0.04em; }
  .spotrulelbl { fill: var(--muted); }
  .liverulelbl { fill: var(--accent); font-size: var(--svg-label-font-primary); font-weight: 700; }
  .clampedlbl { fill: var(--stale-ink); font-weight: 700; }
  /* A stated absence, never a blank where a line should be. */
  .spotunplaced {
    margin: 0 0 6px;
    color: var(--stale-ink);
    font-family: var(--font-num);
    font-size: 10px;
    line-height: 1.4;
  }
  /* The bounded viewport. Every served row stays in the DOM; only what is SHOWN is capped. */
  .ladderview { max-height: 420px; overflow-y: auto; overscroll-behavior: contain; }
  /*
    THE SPOT HEAD. One big number, tabular so it does not jitter as the last digit ticks, and a
    quiet meta column beside it. Tokens only — law 8, both themes come free.
  */
  .spothead { display: flex; align-items: baseline; gap: 8px; margin: 2px 0 4px; flex-wrap: wrap; }
  .spotval {
    font: 600 26px/1.05 var(--font-num);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
    color: var(--ink);
  }
  /* An unserved spot is a STATED absence, never a blank space where a price should be. */
  .spotval.unserved { color: var(--stale-ink); font-weight: 400; }
  .spotmeta { display: flex; flex-direction: column; gap: 1px; }
  .spotlbl { font: 10px var(--font-num); color: var(--faint); letter-spacing: 0.06em; }
  .spotage { font: 10px var(--font-num); color: var(--muted); }
  .spotage.stale { color: var(--stale-ink); background: var(--stale-soft); border-radius: var(--radius-chip); padding: 0 4px; }
  .spotage.unserved { color: var(--stale-ink); }
  .livehead {
    margin-left: auto;
    color: var(--accent);
    font: 700 var(--label-font-primary)/1 var(--font-num);
    font-variant-numeric: tabular-nums;
  }
  .liveage { color: var(--muted); font: var(--label-font-micro) var(--font-num); }
  .livehead.absent, .liveage.absent { color: var(--stale-ink); }
  .scrubber { display: flex; align-items: center; gap: 6px; }
  .srlabel { font: 10px var(--font-num); color: var(--faint); }
  .scrubber input[type="range"] { flex: 1; min-width: 0; accent-color: var(--accent); height: 14px; }
  .scrubber input[type="range"]:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .livebtn { font: 10px var(--font-num); color: var(--muted); background: transparent;
             border: 1px solid var(--line); border-radius: var(--radius-chip); padding: 1px 6px; cursor: pointer; }
  .livebtn.on { color: var(--ink); border-color: var(--accent); }
  .livebtn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .lgd2 { display: flex; gap: 10px; flex-wrap: wrap; margin: 0; font-size: 10px; color: var(--muted); }
  .lgd2 i { display: inline-block; width: 8px; height: 8px; border-radius: 2px; margin-right: 4px; }
  .lgd2 i.pos { background: var(--up); } .lgd2 i.neg { background: var(--dn); }
  .lgd2 .hyp { color: var(--faint); }
  .lgd2 .gl b { font-weight: 400; }
  .lgd2 .gl b.add, .lgd2 .gl b.pull { color: var(--ink); }
  .lgd2 .gl b.flat { color: var(--faint); }
  .lgd2 .gl em { font-style: normal; color: var(--faint); }
</style>
